import { cn } from '../../lib/utils'

interface StatusBadgeProps {
  status: string
  size?: 'sm' | 'md'
  className?: string
}

/** Normalize status key: lowercase, replace spaces/underscores with _ */
function normalize(s: string): string {
  return s.toLowerCase().replace(/[\s-]+/g, '_')
}

/** Display label: replace underscores with spaces, capitalize each word */
function displayLabel(s: string): string {
  return s
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

type ColorDef = { badge: string; dot: string }

const STATUS_COLORS: Record<string, ColorDef> = {
  // From data/StatusBadge (Design-v2)
  new:              { badge: 'bg-sky-500/10 text-sky-400 border-sky-500/20',       dot: 'text-sky-400' },
  in_review:        { badge: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',    dot: 'text-cyan-400' },
  resolved:         { badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', dot: 'text-emerald-400' },
  escalated:        { badge: 'bg-orange-500/10 text-orange-400 border-orange-500/20',    dot: 'text-orange-400' },
  pending:          { badge: 'bg-amber-500/10 text-amber-400 border-amber-500/20',       dot: 'text-amber-400' },
  approved:         { badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', dot: 'text-emerald-400' },
  rejected:         { badge: 'bg-red-500/10 text-red-400 border-red-500/20',             dot: 'text-red-400' },

  // From finance/StatusBadge
  reviewing:        { badge: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',    dot: 'text-cyan-400' },
  detected:         { badge: 'bg-amber-500/10 text-amber-400 border-amber-500/20', dot: 'text-amber-400' },
  classified:       { badge: 'bg-blue-500/10 text-blue-400 border-blue-500/20',    dot: 'text-blue-400' },
  resolved_auto:    { badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', dot: 'text-emerald-400' },
  resolved_human:   { badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', dot: 'text-emerald-400' },
  pending_approval: { badge: 'bg-amber-500/10 text-amber-400 border-amber-500/20',       dot: 'text-amber-400' },
  completed:        { badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', dot: 'text-emerald-400' },

  // ERP / workflow statuses
  draft:            { badge: 'bg-gray-500/10 text-gray-400 border-gray-500/20',    dot: 'text-gray-400' },
  submitted:        { badge: 'bg-blue-500/10 text-blue-400 border-blue-500/20',    dot: 'text-blue-400' },
  paid:             { badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', dot: 'text-emerald-400' },
  overdue:          { badge: 'bg-red-500/10 text-red-400 border-red-500/20',       dot: 'text-red-400' },
  cancelled:        { badge: 'bg-gray-500/10 text-gray-400 border-gray-500/20',    dot: 'text-gray-400' },
  complete:         { badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', dot: 'text-emerald-400' },
  failed:           { badge: 'bg-red-500/10 text-red-400 border-red-500/20',       dot: 'text-red-400' },
  running:          { badge: 'bg-blue-500/10 text-blue-400 border-blue-500/20',    dot: 'text-blue-400' },
  paused_human:     { badge: 'bg-purple-500/10 text-purple-400 border-purple-500/20',   dot: 'text-purple-400' },
  skipped:          { badge: 'bg-gray-500/10 text-gray-400 border-gray-500/20',    dot: 'text-gray-400' },

  // Severity helpers
  high:             { badge: 'bg-red-500/10 text-red-400 border-red-500/20',       dot: 'text-red-400' },
  medium:           { badge: 'bg-amber-500/10 text-amber-400 border-amber-500/20', dot: 'text-amber-400' },
  low:              { badge: 'bg-blue-500/10 text-blue-400 border-blue-500/20',    dot: 'text-blue-400' },
  critical:         { badge: 'bg-red-500/10 text-red-400 border-red-500/20',       dot: 'text-red-400' },
}

const FALLBACK: ColorDef = {
  badge: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
  dot: 'text-gray-400',
}

export default function StatusBadge({ status, size = 'sm', className }: StatusBadgeProps) {
  const key = normalize(status)
  const colors = STATUS_COLORS[key] || FALLBACK

  return (
    <span className={cn(
      'inline-flex items-center gap-1 rounded-full border font-medium',
      size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs',
      colors.badge,
      className,
    )}>
      <span className={cn('text-[8px]', colors.dot)}>●</span>
      {displayLabel(key)}
    </span>
  )
}
